/**
 * Kịch bản thiên tai tham chiếu theo năm phân tích: mức nghiêm trọng quốc gia, trọng số miền,
 * và các ngày có phủ trên timeline (đồng bộ cửa sổ mưa–lũ tiêu biểu trong báo cáo công khai).
 *
 * **Phương pháp:** `nationalSeverity` (0–1), `regionWeights` (≈1 = trung bình),
 * `depthScaleByRegion` (hệ số nhân độ sâu m theo miền/năm) và
 * `approxNationalAffectedThousands` là **thứ tự độ lớn định tính** để đồng bộ UI/KPI
 * với các năm có lũ/bão nổi bật trong thống kê công khai (EM-DAT, ReliefWeb, báo cáo
 * phòng chống thiên tai, tổng hợp báo chí đối chiếu). **Không** thay thế số liệu vận hành
 * hay cảnh báo hành chính.
 */

/** Trọng số tương đối theo miền (≈1 = trung bình). */
export interface FloodRegionWeights {
  north: number;
  central: number;
  south: number;
  highlands: number;
}

export interface FloodYearScenario {
  /** 0–1: điều chỉnh biên độ KPI / lưới toàn cục. */
  nationalSeverity: number;
  regionWeights: FloodRegionWeights;
  /**
   * Hệ số nhân **độ sâu (m)** theo miền — ~1 = giữ nguyên so với `maxDepth` / `depth_m` nguồn.
   * Căn chỉnh định tính với thứ tự độ lớn lịch sử (vd. ngập đô thị Bắc khi bão Yagi ~0,2–0,5 m trong báo cáo so với cực trị trong lớp nền).
   */
  depthScaleByRegion?: Partial<FloodRegionWeights>;
  /**
   * Các **cửa sổ ngày** (YYYY-MM-DD) được phép trên thanh thời gian — tập hợp từ nhiều
   * khoảng đợt mưa–lũ tiêu biểu, **không** phải mọi ngày trong năm và không ám chỉ ngập liên tục.
   * Rỗng → fallback tháng 6–11 trong {@link floodTimelineCalendar}.
   */
  coverageDays: readonly string[];
  /** Gợi ý nguồn tham chiếu (metadata / chú thích nguồn trong UI). */
  referenceNote: string;
  /** Một dòng tóm tắt đợt mưa/lũ tiêu biểu (tham chiếu thống kê công khai). */
  headlineSummary?: string;
  /**
   * Ước tính quy mô ảnh hưởng dân cư — đơn vị: **nghìn người** (thứ tự độ lớn, không dùng làm cảnh báo hành chính).
   */
  approxNationalAffectedThousands?: number;
}

const DEFAULT_SCENARIO: FloodYearScenario = {
  nationalSeverity: 0.62,
  regionWeights: {
    north: 1,
    central: 1,
    south: 1,
    highlands: 1,
  },
  coverageDays: [],
  referenceNote: 'Tham chiếu thống kê thiên tai công khai (trung bình nhiều năm)',
  headlineSummary: 'Mùa mưa trung bình — tham chiếu tổng hợp nhiều năm',
  approxNationalAffectedThousands: 180,
};

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Mỗi phần tử: [tháng, ngày_bắt_đầu, ngày_kết_thúc] trong cùng một năm `year`.
 * Nhiều phần tử = nhiều **cửa sổ** (đợt) — có khoảng trống giữa các đợt; không bắt buộc liền mạch.
 */
function daysFromMonthRanges(
  year: number,
  ranges: readonly (readonly [number, number, number])[],
): string[] {
  const out = new Set<string>();
  for (const [month, dayStart, dayEnd] of ranges) {
    for (let d = dayStart; d <= dayEnd; d += 1) {
      out.add(`${year}-${pad2(month)}-${pad2(d)}`);
    }
  }
  return [...out].sort();
}

/**
 * Bảng theo năm phân tích (UI). Mỗi năm căn theo **thứ tự độ lớn** các đợt nổi bật trong
 * thống kê công khai (vd. lũ Trung Bộ 2020; Yagi 2024; Damrey 2017; Noru 2022).
 * `coverageDays` gồm **vài cửa sổ ngày** (đợt mưa–lũ tham chiếu), không phủ trọn mùa —
 * người dùng chỉ chọn được ngày trong các cửa sổ đó trên timeline.
 */
const FLOOD_SCENARIO_BY_YEAR: Partial<Record<number, FloodYearScenario>> = {
  2017: {
    nationalSeverity: 0.82,
    regionWeights: { north: 0.94, central: 1.24, south: 1.04, highlands: 0.94 },
    depthScaleByRegion: { central: 1.06, south: 1.04, north: 1, highlands: 1 },
    coverageDays: [
      ...daysFromMonthRanges(2017, [
        [7, 20, 28],
        [8, 1, 6],
        [10, 25, 31],
        [11, 1, 12],
      ]),
    ],
    referenceNote:
      'Đầu mùa: mưa lớn Bắc Bộ; cuối năm: bão Damrey (đổ bộ đầu tháng 11) — mưa gió rất mạnh dải Nam Trung Bộ–Tây Nguyên, ngập úng ven biển và sạt lở; thiệt hại ghi nhận ở quy mô cao trong thống kê công khai (EM-DAT, báo cáo thiên tai Việt Nam).',
    headlineSummary:
      'Bão Damrey và mưa lớn cuối năm — Trung Bộ và Nam Trung Bộ chịu ngập úng rộng (tham chiếu thống kê công khai).',
    approxNationalAffectedThousands: 310,
  },
  2018: {
    nationalSeverity: 0.71,
    regionWeights: { north: 1.12, central: 1.08, south: 0.92, highlands: 0.94 },
    coverageDays: [
      ...daysFromMonthRanges(2018, [
        [6, 22, 30],
        [7, 15, 25],
        [8, 1, 10],
        [9, 1, 12],
        [10, 5, 15],
      ]),
    ],
    referenceNote:
      'Bão Son Tinh gây mưa lớn và ngập úng miền Bắc (tháng 7); thêm các đợt mưa lũ xen kẽ Trung–Nam. Mức năm: cao so trung bình dài hạn, dưới đỉnh lịch sử 2020. Tham chiếu báo cáo mùa mưa bão và tổng hợp thiên tai.',
    headlineSummary:
      'Son Tinh và mưa lũ nhiều đợt — Bắc Bộ và Trung Bộ chịu nặng (tham chiếu báo cáo tổng hợp).',
    approxNationalAffectedThousands: 220,
  },
  2019: {
    nationalSeverity: 0.58,
    regionWeights: { north: 0.92, central: 0.96, south: 0.98, highlands: 0.92 },
    coverageDays: [
      ...daysFromMonthRanges(2019, [
        [7, 10, 18],
        [8, 8, 15],
        [9, 5, 12],
        [10, 5, 12],
      ]),
    ],
    referenceNote:
      'Mùa mưa tương đối ôn hòa so với các năm lân cận; lũ cục bộ vẫn xảy ra nhưng thiệt hại tổng thể thấp hơn giai đoạn 2016–2018 và thấp hơn rõ so với đỉnh lũ Trung Bộ 2020. Tham chiếu tổng hợp thống kê thiên tai.',
    headlineSummary: 'Mùa mưa nhẹ–vừa — ít đợt cực đoan trên diện rộng so với trung bình dài hạn.',
    approxNationalAffectedThousands: 140,
  },
  2020: {
    nationalSeverity: 0.99,
    regionWeights: { north: 0.82, central: 1.52, south: 0.74, highlands: 0.82 },
    depthScaleByRegion: { central: 1.12, north: 0.9, south: 0.92, highlands: 0.94 },
    coverageDays: [
      ...daysFromMonthRanges(2020, [
        [9, 1, 18],
        [9, 22, 30],
        [10, 5, 18],
        [10, 22, 31],
        [11, 1, 14],
      ]),
    ],
    referenceNote:
      'Lũ lịch sử miền Trung: chuỗi bão–mưa tháng 9–11 với nhiều đợt chồng lấn (thống kê công khai ghi nhận ảnh hưởng diện rộng). Timeline chỉ mở các **cửa sổ ngày** tham chiếu từng giai đoạn đỉnh — không ám chỉ ngập suốt cả năm. Trọng số miền Trung tối đa trong bảng.',
    headlineSummary:
      'Lũ lịch sử miền Trung — mưa kéo dài tháng 9–11, ngập diện rộng (tham chiếu thống kê thiên tai 2020).',
    approxNationalAffectedThousands: 4200,
  },
  2021: {
    nationalSeverity: 0.56,
    regionWeights: { north: 0.94, central: 0.96, south: 0.94, highlands: 0.9 },
    coverageDays: [
      ...daysFromMonthRanges(2021, [
        [7, 18, 31],
        [8, 15, 26],
        [9, 10, 22],
        [10, 8, 18],
      ]),
    ],
    referenceNote:
      'Sau đỉnh 2020, mùa mưa 2021 chủ yếu lũ cục bộ và ngập úng tại một số lưu vực; quy mô tổng thể và trọng số Trung Bộ thấp hơn rõ so với 2020.',
    headlineSummary: 'Mưa lũ cục bộ — quy mô tổng thể nhỏ hơn nhiều so với đỉnh lũ 2020.',
    approxNationalAffectedThousands: 155,
  },
  2022: {
    nationalSeverity: 0.74,
    regionWeights: { north: 0.96, central: 1.14, south: 0.88, highlands: 0.96 },
    coverageDays: [
      ...daysFromMonthRanges(2022, [
        [7, 18, 31],
        [8, 12, 24],
        [9, 24, 30],
        [10, 1, 14],
      ]),
    ],
    referenceNote:
      'Bão Noru (cuối tháng 9) — một trong các cơn bão mạnh ảnh hưởng trực tiếp đất liền; mưa gió lớn Trung Bộ–Nam Trung Bộ. Cửa sổ timeline nhấn cuối tháng 9. Tham chiếu báo cáo mùa bão và thống kê thiệt hại.',
    headlineSummary:
      'Bão Noru — mưa gió lớn cuối tháng 9, Trung Bộ chịu nặng (tham chiếu báo cáo).',
    approxNationalAffectedThousands: 275,
  },
  2023: {
    nationalSeverity: 0.62,
    regionWeights: { north: 0.94, central: 1.02, south: 0.96, highlands: 0.92 },
    coverageDays: [
      ...daysFromMonthRanges(2023, [
        [7, 15, 31],
        [8, 12, 26],
        [9, 10, 24],
        [10, 10, 22],
      ]),
    ],
    referenceNote:
      'Mùa mưa bão với nhiều đợt mưa lớn cục bộ; một số tình huống áp thấp/dải mưa kéo dài Trung Bộ nhưng không đạt mức tổng thể của 2020. Tham chiếu tổng hợp năm.',
    headlineSummary:
      'Mưa lũ cục bộ và dải mưa kéo — Trung Bộ trên mức trung bình nhưng dưới đỉnh 2020.',
    approxNationalAffectedThousands: 175,
  },
  2024: {
    nationalSeverity: 0.83,
    regionWeights: { north: 1.28, central: 0.9, south: 0.84, highlands: 0.98 },
    /**
     * Bắc Bộ: ngập đô thị điển hình sau Yagi ~0,2–0,5 m trong báo cáo — hệ số nhỏ hơn 1 để
     * khớp thứ tự độ lớn đó so với cực trị trong lớp nền không gian.
     */
    depthScaleByRegion: { north: 0.42, central: 1, south: 1, highlands: 0.95 },
    coverageDays: [
      ...daysFromMonthRanges(2024, [
        [8, 25, 31],
        [9, 1, 16],
        [9, 22, 30],
        [10, 1, 12],
      ]),
    ],
    referenceNote:
      'Bão Yagi (đầu tháng 9): mưa gió cực đoan miền Bắc và đồng bằng sông Hồng; ngập úng đô thị, sạt lở vùng núi. Trọng số Bắc Bộ cao nhất trong bảng các năm gần đây; Trung–Nam tương đối thấp hơn so với năm lũ Trung Bộ điển hình. Tham chiếu thống kê công khai 2024.',
    headlineSummary:
      'Bão Yagi — mưa gió cực đoan miền Bắc và đồng bằng (tham chiếu thống kê công khai 2024).',
    approxNationalAffectedThousands: 480,
  },
  2025: {
    nationalSeverity: 0.74,
    regionWeights: { north: 0.98, central: 1.1, south: 0.98, highlands: 1.02 },
    coverageDays: [
      ...daysFromMonthRanges(2025, [
        [6, 15, 30],
        [7, 18, 31],
        [8, 12, 28],
        [9, 10, 25],
        [10, 5, 22],
      ]),
    ],
    referenceNote:
      'Tham số tổng hợp cho năm phân tích hiện tại: đa đợt mưa bão trải Bắc–Trung–Nam, mức trung bình–cao; sẽ cần cập nhật khi có báo cáo thiên tai đầy đủ sau mùa.',
    headlineSummary:
      'Nhiều đợt mưa bão — phân bổ đa miền (tham chiếu tổng hợp; cập nhật theo báo cáo sau mùa).',
    approxNationalAffectedThousands: 290,
  },
  2026: {
    nationalSeverity: 0.66,
    regionWeights: { north: 0.94, central: 1.02, south: 0.94, highlands: 0.96 },
    coverageDays: [
      ...daysFromMonthRanges(2026, [
        [6, 15, 28],
        [7, 15, 31],
        [8, 10, 26],
        [9, 10, 24],
        [10, 5, 20],
      ]),
    ],
    referenceNote:
      'Tham chiếu tổng hợp nhiều năm cho năm phân tích được chọn; cập nhật khi có báo cáo thiên tai chính thức hoặc kịch bản khí hậu–thủy văn mới.',
    headlineSummary:
      'Mùa mưa — tham chiếu kịch bản tổng hợp; cập nhật theo số liệu và chỉ đạo nghiệp vụ sau mùa.',
    approxNationalAffectedThousands: 205,
  },
};

/**
 * Kịch bản cho một năm phân tích; năm không khai báo dùng {@link DEFAULT_SCENARIO}.
 */
export function getFloodYearScenario(year: number): FloodYearScenario {
  return FLOOD_SCENARIO_BY_YEAR[year] ?? DEFAULT_SCENARIO;
}

/**
 * Hệ số cường độ toàn cục (≈0.55–1.05) để nhân KPI / điều chỉnh lưới.
 */
export function getNationalFloodIntensityFactor(year: number): number {
  const s = getFloodYearScenario(year).nationalSeverity;
  return 0.52 + 0.53 * s;
}

/**
 * Trọng số miền cho một zone (generator lưới / vùng).
 */
export type FloodCalibrationZoneRegion = 'north' | 'central' | 'south' | 'highlands';

export function getRegionWeightForYear(year: number, region: FloodCalibrationZoneRegion): number {
  const w = getFloodYearScenario(year).regionWeights;
  return w[region];
}

/**
 * Hệ số nhân độ sâu (m) cho miền và năm phân tích — mặc định 1 nếu không khai báo.
 */
export function getDepthScaleForYear(year: number, region: FloodCalibrationZoneRegion): number {
  const p = getFloodYearScenario(year).depthScaleByRegion;
  const v = p?.[region];
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 1;
}

/** Ngày có phủ (sắp xếp); rỗng nếu dùng fallback mùa. */
export function getFloodCoverageDaysForYear(year: number): readonly string[] {
  return getFloodYearScenario(year).coverageDays;
}

/**
 * Set tra cứu nhanh ngày có phủ.
 */
export function getFloodCoverageDaySetForYear(year: number): ReadonlySet<string> {
  const days = getFloodCoverageDaysForYear(year);
  return days.length > 0 ? new Set(days) : new Set<string>();
}

/** Ghi chú tham chiếu cho metadata UI. */
export function getFloodScenarioReferenceNote(year: number): string {
  return getFloodYearScenario(year).referenceNote;
}

/** Các năm có ít nhất một ngày phủ tường minh trong bảng. */
export function yearUsesExplicitCoverageDays(year: number): boolean {
  return getFloodCoverageDaysForYear(year).length > 0;
}

/** Tóm tắt một dòng cho sidebar (luôn có chuỗi — fallback từ mặc định). */
export function getFloodHeadlineSummary(year: number): string {
  return getFloodYearScenario(year).headlineSummary ?? DEFAULT_SCENARIO.headlineSummary ?? '';
}

/**
 * Quy mô ảnh hưởng ước tính — **nghìn người** (thứ tự độ lớn, tham chiếu thống kê).
 */
export function getApproxNationalAffectedThousands(year: number): number {
  return (
    getFloodYearScenario(year).approxNationalAffectedThousands ??
    DEFAULT_SCENARIO.approxNationalAffectedThousands ??
    0
  );
}
